import { defineStore } from 'pinia'
import { ref } from 'vue'
import { searchTasks } from '@/composables/services/useTasksApi'
import { buildTaskViewRequest, buildOwnerUserIdFallbackRequest, buildTeamTasksFallbackRequest } from '@/modules/inbox/utils/taskSearchRequests'
import { filterTasksByOwner, filterTasksByTeamUnassigned } from '@/modules/inbox/utils/taskHelpers'
import { useUserStore } from '@/stores/user'
import type { Task } from '@/types/task'

const PAGE_SIZE = 25
// Load all tasks (primary + fallback) with limit:100 for user-scoped views so
// deduplication covers the full result set and the badge count matches the list.
// API enforces a max limit of 100.
//
// TODO: Once the API supports compound link filtering (OR across link fields),
// revert the primary limit back to PAGE_SIZE (25) and restore separate limit:0
// count queries in useInboxViewCounts.ts. The dual-request + dedup pattern and
// FALLBACK_LIMIT can then be removed entirely.
const FALLBACK_LIMIT = 100

function getCtx() {
  const userStore = useUserStore()
  return {
    userId: userStore.userData?.id || '',
    orgId: userStore.userData?.object_id || '',
    teamIds: (userStore.userTeams || [])
      .map((team: any) => team?.team_id || team)
      .filter(Boolean) as string[],
  }
}

function deduplicateTasks(primary: Task[], secondary: Task[]): Task[] {
  const seen = new Set<string>()
  const merged: Task[] = []
  for (const t of [...primary, ...secondary]) {
    if (!seen.has(t.id)) {
      seen.add(t.id)
      merged.push(t)
    }
  }
  return merged
}

export const useTasksStore = defineStore('tasks', () => {
  // Task lists
  const userTasks = ref<Task[]>([])
  const userTasksDueToday = ref<Task[]>([])
  const teamTasks = ref<Task[]>([])
  const allTasks = ref<Task[]>([])

  // Initial load state
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Per-view: hasMore / loadingMore for infinite scroll
  const hasMoreUserTasks = ref(false)
  const hasMoreUserTasksDueToday = ref(false)
  const hasMoreTeamTasks = ref(false)
  const hasMoreAllTasks = ref(false)

  const loadingMoreUserTasks = ref(false)
  const loadingMoreUserTasksDueToday = ref(false)
  const loadingMoreTeamTasks = ref(false)
  const loadingMoreAllTasks = ref(false)

  // Primary-source start offsets for merged views (not reactive — internal cursor)
  let _userTasksPrimaryStart = 0
  let _userTasksDueTodayPrimaryStart = 0
  let _teamTasksPrimaryStart = 0

  // Inflight deduplication — one request per view at a time
  let _userTasksInflight: Promise<void> | null = null
  let _userTasksDueTodayInflight: Promise<void> | null = null
  let _teamTasksInflight: Promise<void> | null = null
  let _allTasksInflight: Promise<void> | null = null

  // ─── Initial load functions ────────────────────────────────────────────────

  async function loadUserTasks() {
    if (_userTasksInflight) return _userTasksInflight

    _userTasksInflight = (async () => {
      loading.value = true
      error.value = null
      _userTasksPrimaryStart = 0
      try {
        const ctx = getCtx()
        const [primary, fallback] = await Promise.all([
          searchTasks(buildTaskViewRequest('my-tasks', ctx, { limit: FALLBACK_LIMIT, start: 0 })),
          searchTasks(buildOwnerUserIdFallbackRequest('my-tasks', ctx, { limit: FALLBACK_LIMIT, start: 0 })),
        ])
        _userTasksPrimaryStart = primary.data?.length ?? 0
        const merged = deduplicateTasks(primary.data ?? [], fallback.data ?? [])
        userTasks.value = filterTasksByOwner(merged, ctx.userId)
        hasMoreUserTasks.value = primary.meta.hasMore
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load user tasks'
      } finally {
        loading.value = false
        _userTasksInflight = null
      }
    })()

    return _userTasksInflight
  }

  async function loadUserTasksDueToday() {
    if (_userTasksDueTodayInflight) return _userTasksDueTodayInflight

    _userTasksDueTodayInflight = (async () => {
      loading.value = true
      error.value = null
      _userTasksDueTodayPrimaryStart = 0
      try {
        const ctx = getCtx()
        const [primary, fallback] = await Promise.all([
          searchTasks(buildTaskViewRequest('my-tasks-due-today', ctx, { limit: FALLBACK_LIMIT, start: 0 })),
          searchTasks(buildOwnerUserIdFallbackRequest('my-tasks-due-today', ctx, { limit: FALLBACK_LIMIT, start: 0 })),
        ])
        _userTasksDueTodayPrimaryStart = primary.data?.length ?? 0
        const merged = deduplicateTasks(primary.data ?? [], fallback.data ?? [])
        userTasksDueToday.value = filterTasksByOwner(merged, ctx.userId)
        hasMoreUserTasksDueToday.value = primary.meta.hasMore
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load tasks due today'
      } finally {
        loading.value = false
        _userTasksDueTodayInflight = null
      }
    })()

    return _userTasksDueTodayInflight
  }

  async function loadTeamTasks() {
    if (_teamTasksInflight) return _teamTasksInflight

    _teamTasksInflight = (async () => {
      loading.value = true
      error.value = null
      _teamTasksPrimaryStart = 0
      try {
        const ctx = getCtx()
        if (!ctx.teamIds.length) {
          teamTasks.value = []
          hasMoreTeamTasks.value = false
          return
        }
        const [primary, fallback] = await Promise.all([
          searchTasks(buildTaskViewRequest('team-tasks', ctx, { limit: FALLBACK_LIMIT, start: 0 })),
          searchTasks(buildTeamTasksFallbackRequest(ctx, { limit: FALLBACK_LIMIT, start: 0 })),
        ])
        _teamTasksPrimaryStart = primary.data?.length ?? 0
        const merged = deduplicateTasks(primary.data ?? [], fallback.data ?? [])
        teamTasks.value = filterTasksByTeamUnassigned(merged)
        hasMoreTeamTasks.value = primary.meta.hasMore
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load team tasks'
        console.error('TasksStore: Error in loadTeamTasks:', err)
      } finally {
        loading.value = false
        _teamTasksInflight = null
      }
    })()

    return _teamTasksInflight
  }

  async function loadAllTasks() {
    if (_allTasksInflight) return _allTasksInflight

    _allTasksInflight = (async () => {
      loading.value = true
      error.value = null
      try {
        const ctx = getCtx()
        const res = await searchTasks(buildTaskViewRequest('all-tasks', ctx, { limit: PAGE_SIZE, start: 0 }))
        allTasks.value = res.data ?? []
        hasMoreAllTasks.value = res.meta.hasMore
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load all tasks'
        console.error('TasksStore: Error in loadAllTasks:', err)
      } finally {
        loading.value = false
        _allTasksInflight = null
      }
    })()

    return _allTasksInflight
  }

  // ─── Load more (pagination) ────────────────────────────────────────────────

  async function loadMoreUserTasks() {
    if (loadingMoreUserTasks.value || !hasMoreUserTasks.value) return
    loadingMoreUserTasks.value = true
    try {
      const ctx = getCtx()
      const primary = await searchTasks(
        buildTaskViewRequest('my-tasks', ctx, { limit: PAGE_SIZE, start: _userTasksPrimaryStart }),
      )
      _userTasksPrimaryStart += primary.data?.length ?? 0
      const existingIds = new Set(userTasks.value.map(t => t.id))
      const newItems = filterTasksByOwner(
        (primary.data ?? []).filter(t => !existingIds.has(t.id)),
        ctx.userId,
      )
      userTasks.value = [...userTasks.value, ...newItems]
      hasMoreUserTasks.value = primary.meta.hasMore
    } catch (err) {
      console.error('TasksStore: Error in loadMoreUserTasks:', err)
    } finally {
      loadingMoreUserTasks.value = false
    }
  }

  async function loadMoreUserTasksDueToday() {
    if (loadingMoreUserTasksDueToday.value || !hasMoreUserTasksDueToday.value) return
    loadingMoreUserTasksDueToday.value = true
    try {
      const ctx = getCtx()
      const primary = await searchTasks(
        buildTaskViewRequest('my-tasks-due-today', ctx, { limit: PAGE_SIZE, start: _userTasksDueTodayPrimaryStart }),
      )
      _userTasksDueTodayPrimaryStart += primary.data?.length ?? 0
      const existingIds = new Set(userTasksDueToday.value.map(t => t.id))
      const newItems = filterTasksByOwner(
        (primary.data ?? []).filter(t => !existingIds.has(t.id)),
        ctx.userId,
      )
      userTasksDueToday.value = [...userTasksDueToday.value, ...newItems]
      hasMoreUserTasksDueToday.value = primary.meta.hasMore
    } catch (err) {
      console.error('TasksStore: Error in loadMoreUserTasksDueToday:', err)
    } finally {
      loadingMoreUserTasksDueToday.value = false
    }
  }

  async function loadMoreTeamTasks() {
    if (loadingMoreTeamTasks.value || !hasMoreTeamTasks.value) return
    loadingMoreTeamTasks.value = true
    try {
      const ctx = getCtx()
      const res = await searchTasks(
        buildTaskViewRequest('team-tasks', ctx, { limit: PAGE_SIZE, start: _teamTasksPrimaryStart }),
      )
      _teamTasksPrimaryStart += res.data?.length ?? 0
      const existingIds = new Set(teamTasks.value.map(t => t.id))
      const newItems = filterTasksByTeamUnassigned(
        (res.data ?? []).filter(t => !existingIds.has(t.id)),
      )
      teamTasks.value = [...teamTasks.value, ...newItems]
      hasMoreTeamTasks.value = res.meta.hasMore
    } catch (err) {
      console.error('TasksStore: Error in loadMoreTeamTasks:', err)
    } finally {
      loadingMoreTeamTasks.value = false
    }
  }

  async function loadMoreAllTasks() {
    if (loadingMoreAllTasks.value || !hasMoreAllTasks.value) return
    loadingMoreAllTasks.value = true
    try {
      const ctx = getCtx()
      const res = await searchTasks(
        buildTaskViewRequest('all-tasks', ctx, { limit: PAGE_SIZE, start: allTasks.value.length }),
      )
      const existingIds = new Set(allTasks.value.map(t => t.id))
      const newItems = (res.data ?? []).filter(t => !existingIds.has(t.id))
      allTasks.value = [...allTasks.value, ...newItems]
      hasMoreAllTasks.value = res.meta.hasMore
    } catch (err) {
      console.error('TasksStore: Error in loadMoreAllTasks:', err)
    } finally {
      loadingMoreAllTasks.value = false
    }
  }

  // ─── Silent refresh (reset to page 1, no loading spinner) ─────────────────

  let _userTasksSilentInflight: Promise<void> | null = null
  let _userTasksDueTodaySilentInflight: Promise<void> | null = null
  let _teamTasksSilentInflight: Promise<void> | null = null
  let _allTasksSilentInflight: Promise<void> | null = null

  async function loadUserTasksSilently() {
    if (_userTasksSilentInflight) return _userTasksSilentInflight

    _userTasksSilentInflight = (async () => {
      _userTasksPrimaryStart = 0
      try {
        const ctx = getCtx()
        const [primary, fallback] = await Promise.all([
          searchTasks(buildTaskViewRequest('my-tasks', ctx, { limit: FALLBACK_LIMIT, start: 0 })),
          searchTasks(buildOwnerUserIdFallbackRequest('my-tasks', ctx, { limit: FALLBACK_LIMIT, start: 0 })),
        ])
        _userTasksPrimaryStart = primary.data?.length ?? 0
        const merged = deduplicateTasks(primary.data ?? [], fallback.data ?? [])
        userTasks.value = filterTasksByOwner(merged, ctx.userId)
        hasMoreUserTasks.value = primary.meta.hasMore
      } catch (err) {
        console.error('TasksStore: Error in loadUserTasksSilently:', err)
      } finally {
        _userTasksSilentInflight = null
      }
    })()

    return _userTasksSilentInflight
  }

  async function loadUserTasksDueTodaySilently() {
    if (_userTasksDueTodaySilentInflight) return _userTasksDueTodaySilentInflight

    _userTasksDueTodaySilentInflight = (async () => {
      _userTasksDueTodayPrimaryStart = 0
      try {
        const ctx = getCtx()
        const [primary, fallback] = await Promise.all([
          searchTasks(buildTaskViewRequest('my-tasks-due-today', ctx, { limit: FALLBACK_LIMIT, start: 0 })),
          searchTasks(buildOwnerUserIdFallbackRequest('my-tasks-due-today', ctx, { limit: FALLBACK_LIMIT, start: 0 })),
        ])
        _userTasksDueTodayPrimaryStart = primary.data?.length ?? 0
        const merged = deduplicateTasks(primary.data ?? [], fallback.data ?? [])
        userTasksDueToday.value = filterTasksByOwner(merged, ctx.userId)
        hasMoreUserTasksDueToday.value = primary.meta.hasMore
      } catch (err) {
        console.error('TasksStore: Error in loadUserTasksDueTodaySilently:', err)
      } finally {
        _userTasksDueTodaySilentInflight = null
      }
    })()

    return _userTasksDueTodaySilentInflight
  }

  async function loadTeamTasksSilently() {
    if (_teamTasksSilentInflight) return _teamTasksSilentInflight

    _teamTasksSilentInflight = (async () => {
      try {
        _teamTasksPrimaryStart = 0
        const ctx = getCtx()
        if (!ctx.teamIds.length) {
          teamTasks.value = []
          hasMoreTeamTasks.value = false
          return
        }
        const [primary, fallback] = await Promise.all([
          searchTasks(buildTaskViewRequest('team-tasks', ctx, { limit: FALLBACK_LIMIT, start: 0 })),
          searchTasks(buildTeamTasksFallbackRequest(ctx, { limit: FALLBACK_LIMIT, start: 0 })),
        ])
        _teamTasksPrimaryStart = primary.data?.length ?? 0
        const merged = deduplicateTasks(primary.data ?? [], fallback.data ?? [])
        teamTasks.value = filterTasksByTeamUnassigned(merged)
        hasMoreTeamTasks.value = primary.meta.hasMore
      } catch (err) {
        console.error('TasksStore: Error in loadTeamTasksSilently:', err)
      } finally {
        _teamTasksSilentInflight = null
      }
    })()

    return _teamTasksSilentInflight
  }

  async function loadAllTasksSilently() {
    if (_allTasksSilentInflight) return _allTasksSilentInflight

    _allTasksSilentInflight = (async () => {
      try {
        const ctx = getCtx()
        const res = await searchTasks(buildTaskViewRequest('all-tasks', ctx, { limit: PAGE_SIZE, start: 0 }))
        allTasks.value = res.data ?? []
        hasMoreAllTasks.value = res.meta.hasMore
      } catch (err) {
        console.error('TasksStore: Error in loadAllTasksSilently:', err)
      } finally {
        _allTasksSilentInflight = null
      }
    })()

    return _allTasksSilentInflight
  }

  function resetSessionState() {
    userTasks.value = []
    userTasksDueToday.value = []
    teamTasks.value = []
    allTasks.value = []
    hasMoreUserTasks.value = false
    hasMoreUserTasksDueToday.value = false
    hasMoreTeamTasks.value = false
    hasMoreAllTasks.value = false
    _userTasksPrimaryStart = 0
    _userTasksDueTodayPrimaryStart = 0
    _teamTasksPrimaryStart = 0
    _userTasksInflight = null
    _userTasksDueTodayInflight = null
    _teamTasksInflight = null
    _allTasksInflight = null
    _userTasksSilentInflight = null
    _userTasksDueTodaySilentInflight = null
    _teamTasksSilentInflight = null
    _allTasksSilentInflight = null
  }

  return {
    userTasks,
    userTasksDueToday,
    teamTasks,
    allTasks,
    loading,
    error,
    // hasMore
    hasMoreUserTasks,
    hasMoreUserTasksDueToday,
    hasMoreTeamTasks,
    hasMoreAllTasks,
    // loadingMore
    loadingMoreUserTasks,
    loadingMoreUserTasksDueToday,
    loadingMoreTeamTasks,
    loadingMoreAllTasks,
    // initial load
    loadUserTasks,
    loadUserTasksDueToday,
    loadTeamTasks,
    loadAllTasks,
    // load more
    loadMoreUserTasks,
    loadMoreUserTasksDueToday,
    loadMoreTeamTasks,
    loadMoreAllTasks,
    // silent refresh
    loadUserTasksSilently,
    loadUserTasksDueTodaySilently,
    loadTeamTasksSilently,
    loadAllTasksSilently,
    resetSessionState,
  }
})
